// @givmo/board — reading the Form 990 page (CBG-YR5-VIEW; the backend's
// `agent_docs/contracts/board-form-990.md` §4).
//
// ══ WHAT THE READ IS, AND WHAT THIS FILE DOES WITH IT ═══════════════════════════════════════════
//
// One tax year of Part VI: the form's own words (the line map, the IRS's, quoted) and, beside each
// line, the members of `evidence` that bear on it, named by the line's `evidence_kinds`. Facts only:
// the backend serves no sentence about a line, so each line's one-line summary is composed HERE, in
// the words file's words, from those facts and nothing else. A line whose kinds are empty, or whose
// facts hold nothing, reads as a dash.
//
// ⚠ NOTHING HERE ANSWERS A LINE. No count of lines with something beside them, no check, no order by
// what is absent, no colour, no conclusion from a fact (derivation §2.4, §6 I1). A committee's answer is
// the organization's, shown as theirs; the cycles say who was asked and who submitted, never what
// anyone answered; 1b and 2 never combine the answers.
//
// ⚠ THE IRS'S WORDS ARE PASSED THROUGH, never edited, trimmed or re-punctuated (D-CBG-30).
//
// Tolerant, as every Board decode is: a field missing or of the wrong shape never throws. A row that
// cannot be read is dropped; a page whose year, form or evidence cannot be read at all reads as `null`,
// which the page shows as a failed read rather than as a page of dashes that would say Board holds
// nothing.

import type {
  BoardForm990AuthorityAnswerOut,
  BoardForm990BoardMeetingsOut,
  BoardForm990CommitteeMemberOut,
  BoardForm990CommitteeOut,
  BoardForm990CompensationDecisionOut,
  BoardForm990ConflictMeetingOut,
  BoardForm990ConsentOut,
  BoardForm990CycleOut,
  BoardForm990DirectorsOut,
  BoardForm990DocumentVersionOut,
  BoardForm990EvidenceOut,
  BoardForm990FormOut,
  BoardForm990MeetingOut,
  BoardForm990MinutesOut,
  BoardForm990Out,
  BoardForm990PolicyOut,
  BoardForm990ReturnCopyOut,
  BoardForm990ReturnReviewOut,
  BoardForm990SeatOut,
  BoardForm990TaxYearOut,
  BoardForm990YearEndOut,
  BoardPartViInstructionsOut,
  BoardPartViLineOut,
  BoardPartViQuestionOut,
  BoardPartViQuotationOut,
  BoardPartViSectionOut,
} from '@givmo/api-client';
import { displayDate } from '../../lib/display';
import { oneLine, shortDay } from '../conflicts/coi-decode';
import { documentKindLabel } from '../govdocs/govdocs-decode';
import { returnWords as rw } from '../returns/return-words';
import { form990Words as w } from './form990-words';

/**
 * A generated model as the decode hands it on: every optional field filled in, `null` where the read
 * left it out (the director wire's shape, `director/director-wire.ts`), all the way down.
 */
export type Filled<T> = T extends readonly (infer Item)[]
  ? Filled<Item>[]
  : T extends object
    ? { [Key in keyof T]-?: Filled<Exclude<T[Key], undefined>> }
    : T;

export type Form990 = Filled<BoardForm990Out>;
export type Form990YearEnd = Filled<BoardForm990YearEndOut>;
export type Form990TaxYear = Filled<BoardForm990TaxYearOut>;
export type Form990Form = Filled<BoardForm990FormOut>;
export type Form990Section = Filled<BoardPartViSectionOut>;
export type Form990Question = Filled<BoardPartViQuestionOut>;
export type Form990Line = Filled<BoardPartViLineOut>;
export type Form990Instructions = Filled<BoardPartViInstructionsOut>;
export type Form990Quotation = Filled<BoardPartViQuotationOut>;
export type Form990Evidence = Filled<BoardForm990EvidenceOut>;
export type Form990Directors = Filled<BoardForm990DirectorsOut>;
export type Form990Seat = Filled<BoardForm990SeatOut>;
export type Form990Version = Filled<BoardForm990DocumentVersionOut>;
export type Form990BoardMeetings = Filled<BoardForm990BoardMeetingsOut>;
export type Form990Meeting = Filled<BoardForm990MeetingOut>;
export type Form990Minutes = Filled<BoardForm990MinutesOut>;
export type Form990Consent = Filled<BoardForm990ConsentOut>;
export type Form990Committee = Filled<BoardForm990CommitteeOut>;
export type Form990Answer = Filled<BoardForm990AuthorityAnswerOut>;
export type Form990Member = Filled<BoardForm990CommitteeMemberOut>;
export type Form990Policy = Filled<BoardForm990PolicyOut>;
export type Form990Cycle = Filled<BoardForm990CycleOut>;
export type Form990ConflictMeeting = Filled<BoardForm990ConflictMeetingOut>;
export type Form990ReturnCopy = Filled<BoardForm990ReturnCopyOut>;
export type Form990ReturnReview = Filled<BoardForm990ReturnReviewOut>;
export type Form990CompensationDecision = Filled<BoardForm990CompensationDecisionOut>;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isText(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isDay(value: unknown): value is string {
  return typeof value === 'string' && DATE_ONLY.test(value);
}

function textOrNull(value: unknown): string | null {
  return isText(value) ? value : null;
}

function dayOrNull(value: unknown): string | null {
  return isDay(value) ? value : null;
}

function texts(value: unknown): string[] {
  return Array.isArray(value) ? value.filter(isText) : [];
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;
}

function rows<T>(value: unknown, read: (row: Record<string, unknown>) => T | null): T[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((row) => {
    if (!isObject(row)) return [];
    const decoded = read(row);
    return decoded === null ? [] : [decoded];
  });
}

// ── The year ─────────────────────────────────────────────────────────────────────────────────────

export function decodeYearEnd(value: unknown): Form990YearEnd | null {
  if (!isObject(value)) return null;
  const source = value.source;
  if (source !== 'recorded' && source !== 'chosen' && source !== 'none') return null;
  const month = typeof value.month === 'number' ? value.month : null;
  const day = typeof value.day === 'number' ? value.day : null;
  return { source, month, day, recorded_on: dayOrNull(value.recorded_on) };
}

export function decodeTaxYear(value: unknown): Form990TaxYear | null {
  if (!isObject(value)) return null;
  const { begins_on, ends_on, named_year } = value;
  if (!isDay(begins_on) || !isDay(ends_on) || typeof named_year !== 'number') return null;
  return {
    begins_on,
    ends_on,
    named_year,
    short: value.short === true,
    ended: value.ended === true,
  };
}

// ── The form (the IRS's words, passed through) ──────────────────────────────────────────────────

function decodeInstructions(value: unknown): Form990Instructions | null {
  if (!isObject(value)) return null;
  const paragraphs = texts(value.paragraphs);
  if (paragraphs.length === 0 || !isText(value.citation)) return null;
  return { paragraphs, citation: value.citation };
}

function decodeLine(row: Record<string, unknown>): Form990Line | null {
  const instructions = decodeInstructions(row.instructions);
  if (!isText(row.line) || !isText(row.text) || instructions === null) return null;
  return {
    line: row.line,
    label: textOrNull(row.label),
    text: row.text,
    after: textOrNull(row.after),
    choices: texts(row.choices),
    instructions,
    evidence_kinds: texts(row.evidence_kinds),
  };
}

function decodeQuestion(row: Record<string, unknown>): Form990Question | null {
  const lines = rows(row.lines, decodeLine);
  if (!isText(row.number) || lines.length === 0) return null;
  return { number: row.number, stem: textOrNull(row.stem), lines };
}

function decodeSection(row: Record<string, unknown>): Form990Section | null {
  const section = row.section;
  if ((section !== 'A' && section !== 'B' && section !== 'C') || !isText(row.title)) return null;
  return {
    section,
    title: row.title,
    note: textOrNull(row.note),
    instructions: decodeInstructions(row.instructions),
    questions: rows(row.questions, decodeQuestion),
  };
}

export function decodeQuotation(row: Record<string, unknown>): Form990Quotation | null {
  return isText(row.text) && isText(row.citation)
    ? { text: row.text, citation: row.citation }
    : null;
}

function decodeForm(value: unknown): Form990Form | null {
  if (!isObject(value) || typeof value.form_year !== 'number') return null;
  const sections = rows(value.sections, decodeSection);
  if (sections.length === 0) return null;
  return {
    form_year: value.form_year,
    part_title: typeof value.part_title === 'string' ? value.part_title : '',
    header: typeof value.header === 'string' ? value.header : '',
    header_citation: typeof value.header_citation === 'string' ? value.header_citation : '',
    no_committee_quotations: rows(value.no_committee_quotations, decodeQuotation),
    sections,
  };
}

// ── The evidence (§4.3) ──────────────────────────────────────────────────────────────────────────

function decodeSeat(row: Record<string, unknown>): Form990Seat | null {
  if (!isText(row.board_member_id)) return null;
  const term = isObject(row.term)
    ? { starts_on: dayOrNull(row.term.starts_on), ends_on: dayOrNull(row.term.ends_on) }
    : null;
  return {
    board_member_id: row.board_member_id,
    display_name: textOrNull(row.display_name),
    title_labels: texts(row.title_labels),
    basis: row.basis === 'term' ? 'term' : 'roster',
    term,
  };
}

function decodeDirectors(value: unknown): Form990Directors | null {
  if (!isObject(value) || !isDay(value.as_of)) return null;
  return { as_of: value.as_of, seats: rows(value.seats, decodeSeat) };
}

function decodeVersion(row: Record<string, unknown>): Form990Version | null {
  if (!isText(row.record_id) || !isText(row.title)) return null;
  const by =
    isObject(row.adopted_by) && isText(row.adopted_by.record_id) && isText(row.adopted_by.title)
      ? {
          record_id: row.adopted_by.record_id,
          title: row.adopted_by.title,
          occurred_on: dayOrNull(row.adopted_by.occurred_on),
        }
      : null;
  return {
    record_id: row.record_id,
    document_kind: typeof row.document_kind === 'string' ? row.document_kind : '',
    label: textOrNull(row.label),
    title: row.title,
    adopted_on: dayOrNull(row.adopted_on),
    imported: row.imported === true,
    adopted_by: by,
  };
}

const MINUTES_STATES = ['draft', 'submitted', 'approved', 'imported'] as const;
const APPROVAL_WAYS = ['meeting', 'written_consent', 'officer', 'directors_present'] as const;

function decodeMinutes(value: unknown): Form990Minutes | null {
  if (!isObject(value) || !isText(value.record_id)) return null;
  const state = MINUTES_STATES.find((known) => known === value.state);
  if (state === undefined) return null;
  const way = APPROVAL_WAYS.find((known) => known === value.approved_way) ?? null;
  return {
    record_id: value.record_id,
    state,
    approved_on: dayOrNull(value.approved_on),
    approved_way: way,
  };
}

function decodeMeeting(row: Record<string, unknown>): Form990Meeting | null {
  if (!isText(row.meeting_id) || !isDay(row.held_on) || !isDay(row.line_8_on)) return null;
  return {
    meeting_id: row.meeting_id,
    title: typeof row.title === 'string' ? row.title : '',
    meeting_type: typeof row.meeting_type === 'string' ? row.meeting_type : '',
    held_on: row.held_on,
    minutes: decodeMinutes(row.minutes),
    line_8_on: row.line_8_on,
    next_meeting_on: dayOrNull(row.next_meeting_on),
  };
}

function decodeConsent(row: Record<string, unknown>): Form990Consent | null {
  if (!isText(row.consent_id) || !isDay(row.executed_on) || !isDay(row.line_8_on)) return null;
  return {
    consent_id: row.consent_id,
    title: textOrNull(row.title),
    executed_on: row.executed_on,
    record_id: textOrNull(row.record_id),
    line_8_on: row.line_8_on,
    next_meeting_on: dayOrNull(row.next_meeting_on),
  };
}

function decodeBoardMeetings(value: unknown): Form990BoardMeetings | null {
  if (!isObject(value)) return null;
  return {
    meetings: rows(value.meetings, decodeMeeting),
    written_consents: rows(value.written_consents, decodeConsent),
    rule: typeof value.rule === 'string' ? value.rule : '',
  };
}

const CHOICES = ['none', 'particular_matters', 'broad'] as const;

function decodeAnswer(row: Record<string, unknown>): Form990Answer | null {
  const choice = CHOICES.find((known) => known === row.choice_value);
  if (choice === undefined || !isText(row.citation)) return null;
  return {
    choice_value: choice,
    citation: row.citation,
    recorded_at: textOrNull(row.recorded_at),
    superseded_at: textOrNull(row.superseded_at),
  };
}

function decodeMember(row: Record<string, unknown>): Form990Member | null {
  if (!isText(row.board_member_id)) return null;
  return {
    board_member_id: row.board_member_id,
    display_name: textOrNull(row.display_name),
    listed_as_director: row.listed_as_director === true,
    role: row.role === 'chair' ? 'chair' : 'member',
    serves_from: dayOrNull(row.serves_from),
    serves_to: dayOrNull(row.serves_to),
    ended_at: textOrNull(row.ended_at),
  };
}

function decodeCommittee(row: Record<string, unknown>): Form990Committee | null {
  if (!isText(row.committee_id) || !isText(row.name)) return null;
  return {
    committee_id: row.committee_id,
    name: row.name,
    status: row.status === 'dissolved' ? 'dissolved' : 'active',
    established_on: dayOrNull(row.established_on),
    dissolved_on: dayOrNull(row.dissolved_on),
    authority_answers: rows(row.authority_answers, decodeAnswer),
    members: Array.isArray(row.members) ? rows(row.members, decodeMember) : null,
    // CBG-YR6-MEET/-CONSENT: served for a committee answered "Yes", in line 8a's row shapes, each
    // line-8 date against that committee's own next meeting; line 8b opened lists them (VIEW-8B).
    meetings: Array.isArray(row.meetings) ? rows(row.meetings, decodeMeeting) : null,
    written_consents: Array.isArray(row.written_consents)
      ? rows(row.written_consents, decodeConsent)
      : null,
  };
}

const POLICY_KINDS = ['coi_policy', 'whistleblower_policy', 'document_retention_policy'] as const;
export type PolicyKind = (typeof POLICY_KINDS)[number];

function decodePolicy(value: unknown, kind: PolicyKind): Form990Policy {
  if (!isObject(value)) return { document_kind: kind, in_force: null, adopted_in_year: [] };
  return {
    document_kind: kind,
    in_force: isObject(value.in_force) ? decodeVersion(value.in_force) : null,
    adopted_in_year: rows(value.adopted_in_year, decodeVersion),
  };
}

const CYCLE_LINES = ['1b', '2', '12b'] as const;

function decodeCycle(row: Record<string, unknown>): Form990Cycle | null {
  if (!isText(row.cycle_id) || !isText(row.label)) return null;
  if (!isDay(row.period_start) || !isDay(row.period_end)) return null;
  const lines = Array.isArray(row.lines)
    ? CYCLE_LINES.filter((line) => (row.lines as unknown[]).includes(line))
    : [];
  return {
    cycle_id: row.cycle_id,
    label: row.label,
    period_start: row.period_start,
    period_end: row.period_end,
    status: row.status === 'closed' ? 'closed' : 'open',
    opened_at: textOrNull(row.opened_at),
    closed_at: textOrNull(row.closed_at),
    close_record_id: textOrNull(row.close_record_id),
    people_count: count(row.people_count),
    submitted_count: count(row.submitted_count),
    lines,
  };
}

function decodeConflict(row: Record<string, unknown>): Form990ConflictMeeting | null {
  if (!isText(row.meeting_id) || !isDay(row.held_on)) return null;
  return {
    meeting_id: row.meeting_id,
    title: typeof row.title === 'string' ? row.title : '',
    held_on: row.held_on,
    recusal_recorded: row.recusal_recorded === true,
    determination_recorded: row.determination_recorded === true,
  };
}

// CBG-YR5-VIEW-11A and CBG-YR5-REVIEW (`board-return-review.md` §4.3): the year's copies of the
// return provided to the board, and each copy's live link to the board's review. Text-free: no
// motion's words, no file name.

function decodeReturnCopy(row: Record<string, unknown>): Form990ReturnCopy | null {
  if (!isText(row.record_id) || !isDay(row.provided_on)) return null;
  return {
    record_id: row.record_id,
    provided_on: row.provided_on,
    provided_by_display_name: textOrNull(row.provided_by_display_name),
    recipient_count: count(row.recipient_count),
    director_count: count(row.director_count),
    directors_provided_count: count(row.directors_provided_count),
    directors_provided_through: dayOrNull(row.directors_provided_through),
    opened_count: count(row.opened_count),
    printed_copy_count: count(row.printed_copy_count),
    read_recorded_count: count(row.read_recorded_count),
    withdrawn_at: textOrNull(row.withdrawn_at),
    follows_record_id: textOrNull(row.follows_record_id),
    followed_by_record_id: textOrNull(row.followed_by_record_id),
  };
}

const REVIEW_OUTCOMES = ['carried', 'failed'] as const;
const REVIEW_CONSENT_STATUSES = ['pending', 'executed', 'withdrawn'] as const;

function decodeReturnReview(row: Record<string, unknown>): Form990ReturnReview | null {
  if (!isText(row.record_id) || !isDay(row.copy_provided_on)) return null;
  const kind = row.kind === 'motion' || row.kind === 'written_consent' ? row.kind : null;
  if (kind === null) return null;
  const m = isObject(row.motion) ? row.motion : null;
  const outcome = REVIEW_OUTCOMES.find((known) => known === m?.outcome);
  const motion =
    kind === 'motion' && m !== null && isText(m.motion_id) && isText(m.meeting_id) && outcome
      ? {
          motion_id: m.motion_id,
          meeting_id: m.meeting_id,
          meeting_title: textOrNull(m.meeting_title),
          meeting_type: textOrNull(m.meeting_type),
          held_on: dayOrNull(m.held_on),
          outcome,
          votes_for: count(m.votes_for),
          votes_against: count(m.votes_against),
          votes_abstaining: count(m.votes_abstaining),
          minutes_approved_on: dayOrNull(m.minutes_approved_on),
        }
      : null;
  const c = isObject(row.written_consent) ? row.written_consent : null;
  const status = REVIEW_CONSENT_STATUSES.find((known) => known === c?.status);
  const consent =
    kind === 'written_consent' && c !== null && isText(c.written_consent_id) && status
      ? {
          written_consent_id: c.written_consent_id,
          title: textOrNull(c.title),
          consent_date: dayOrNull(c.consent_date),
          status,
          record_id: textOrNull(c.record_id),
        }
      : null;
  if (motion === null && consent === null) return null;
  return {
    record_id: row.record_id,
    copy_provided_on: row.copy_provided_on,
    linked_at: textOrNull(row.linked_at),
    kind,
    motion,
    written_consent: consent,
  };
}

const LINE_15 = ['15a', '15b'] as const;
const PLACEMENTS = ['in_the_year', 'latest_before'] as const;
const ANCHORS = ['motion', 'written_consent', 'record'] as const;

/** A compensation decision placed on line 15a or 15b (the backend's `board-return-review.md` §20). */
function decodeCompensationDecision(
  row: Record<string, unknown>,
): Form990CompensationDecision | null {
  const line = LINE_15.find((known) => known === row.form_990_line);
  const placement = PLACEMENTS.find((known) => known === row.placement);
  const anchor = ANCHORS.find((known) => known === row.anchor_kind);
  if (
    !isText(row.decision_id) ||
    !isText(row.person_key) ||
    !isText(row.person_name) ||
    !isDay(row.decided_on) ||
    line === undefined ||
    placement === undefined ||
    anchor === undefined
  ) {
    return null;
  }
  return {
    decision_id: row.decision_id,
    form_990_line: line,
    person_key: row.person_key,
    person_name: row.person_name,
    position: typeof row.position === 'string' ? row.position : '',
    decided_on: row.decided_on,
    placement,
    anchor_kind: anchor,
    meeting_id: textOrNull(row.meeting_id),
    meeting_title: textOrNull(row.meeting_title),
    written_consent_id: textOrNull(row.written_consent_id),
    consent_title: textOrNull(row.consent_title),
    record_id: textOrNull(row.record_id),
    record_title: textOrNull(row.record_title),
    documents_named: typeof row.documents_named === 'number' ? count(row.documents_named) : null,
    determination_recorded:
      typeof row.determination_recorded === 'boolean' ? row.determination_recorded : null,
    timing_statement: textOrNull(row.timing_statement),
  };
}

function decodeEvidence(value: unknown): Form990Evidence | null {
  if (!isObject(value)) return null;
  const directors = decodeDirectors(value.directors);
  const meetings = decodeBoardMeetings(value.board_meetings);
  if (directors === null || meetings === null) return null;
  return {
    directors,
    governing_document_versions: rows(value.governing_document_versions, decodeVersion),
    board_meetings: meetings,
    committees: rows(value.committees, decodeCommittee),
    coi_policy: decodePolicy(value.coi_policy, 'coi_policy'),
    whistleblower_policy: decodePolicy(value.whistleblower_policy, 'whistleblower_policy'),
    document_retention_policy: decodePolicy(
      value.document_retention_policy,
      'document_retention_policy',
    ),
    // `null` is an answer: the cycles' surface is off, so their lines read as nothing (§4.3.6).
    disclosure_cycles: Array.isArray(value.disclosure_cycles)
      ? rows(value.disclosure_cycles, decodeCycle)
      : null,
    conflicts: rows(value.conflicts, decodeConflict),
    // `null` is an answer: the return's surface is off, so lines 11a and 11b read as nothing.
    return_copies: Array.isArray(value.return_copies)
      ? rows(value.return_copies, decodeReturnCopy)
      : null,
    return_review: Array.isArray(value.return_review)
      ? rows(value.return_review, decodeReturnReview)
      : null,
    // Never null on the wire (§20.4 there): a read without it holds none.
    compensation_decisions: rows(value.compensation_decisions, decodeCompensationDecision),
  };
}

/**
 * The read, or `null` when it cannot be read honestly: no year end, or a tax year whose form or
 * evidence did not come. Rows that cannot be read are dropped; the IRS's words pass through as served.
 */
export function decodeForm990(body: unknown): Form990 | null {
  if (!isObject(body)) return null;
  const yearEnd = decodeYearEnd(body.year_end);
  if (yearEnd === null) return null;
  const taxYear = decodeTaxYear(body.tax_year);
  // A year that came but can't be read, or none with a year end recorded or chosen (the contract's
  // `tax_year` is null only when `year_end.source` is "none", §4.2), is a failed read: the page would
  // otherwise ask for the day, saying no year end is recorded.
  if (taxYear === null && (body.tax_year != null || yearEnd.source !== 'none')) return null;
  const form = taxYear === null ? null : decodeForm(body.form);
  const evidence = taxYear === null ? null : decodeEvidence(body.evidence);
  if (taxYear !== null && (form === null || evidence === null)) return null;
  return {
    year_end: yearEnd,
    tax_years: rows(body.tax_years, decodeTaxYear),
    tax_year: taxYear,
    form,
    evidence,
    disclaimer_class_ids: texts(body.disclaimer_class_ids),
  };
}

// ── The year's words ─────────────────────────────────────────────────────────────────────────────

/** "Jan 1 – Dec 31, 2026", or both years when the tax year spans two ("Jul 1, 2026 – Jun 30, 2027"). */
export function periodWords(beginsOn: string, endsOn: string): string {
  const from =
    beginsOn.slice(0, 4) === endsOn.slice(0, 4) ? shortDay(beginsOn) : displayDate(beginsOn);
  return `${from} – ${displayDate(endsOn)}`;
}

/** The select's option for one tax year: its named year, whether it is short, and its dates. */
export function taxYearOption(year: Form990TaxYear): string {
  return w.yearOption(year.named_year, periodWords(year.begins_on, year.ends_on), year.short);
}

/** "June 30" — a month and day, without a year. Empty for a pair that is not a day. */
export function monthDayWords(month: number | null, day: number | null): string {
  if (month === null || day === null) return '';
  const date = new Date(2000, month - 1, day);
  if (date.getMonth() !== month - 1 || date.getDate() !== day) return '';
  return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}

/** The days a month can hold, in a leap year (the backend accepts February 29, §3.3). */
export function daysInMonth(month: number): number {
  return new Date(2000, month, 0).getDate();
}

// ── Each line's one-line summary, composed from the facts it is served ───────────────────────────

/** The cycles that bear on this line (the read's own `lines`), oldest opened first. */
export function cyclesForLine(
  cycles: readonly Form990Cycle[] | null,
  line: string,
): Form990Cycle[] {
  return (cycles ?? [])
    .filter((cycle) => (cycle.lines as readonly string[]).includes(line))
    .slice()
    .reverse();
}

/** The committee's newest recorded answer, in force or not (the read serves them newest first). */
export function newestAnswer(committee: Form990Committee): Form990Answer | null {
  return committee.authority_answers[0] ?? null;
}

/** The committees an answer of "broad" bears on (line 1a's facts): every one served with members. */
export function broadCommittees(committees: readonly Form990Committee[]): Form990Committee[] {
  return committees.filter(
    (committee) =>
      committee.members !== null &&
      committee.authority_answers.some((answer) => answer.choice_value === 'broad'),
  );
}

function policyKindOf(kinds: readonly string[]): PolicyKind | null {
  return POLICY_KINDS.find((kind) => kinds.includes(kind)) ?? null;
}

function policySummary(policy: Form990Policy, taxYear: Form990TaxYear): string | null {
  const name = w.policyName(policy.document_kind);
  const inForce = policy.in_force;
  if (inForce !== null && inForce.adopted_on !== null) {
    const inYear = inForce.adopted_on >= taxYear.begins_on && inForce.adopted_on <= taxYear.ends_on;
    return inYear
      ? w.adoptedThisYear(name, displayDate(inForce.adopted_on))
      : w.inForceSince(name, displayDate(inForce.adopted_on));
  }
  const latest = policy.adopted_in_year[policy.adopted_in_year.length - 1];
  return latest?.adopted_on ? w.adoptedThisYear(name, displayDate(latest.adopted_on)) : null;
}

function versionsSummary(versions: readonly Form990Version[]): string | null {
  if (versions.length === 0) return null;
  if (versions.length > 1) return w.versionsAdopted(versions.length);
  const [only] = versions;
  if (only === undefined || only.adopted_on === null) return w.versionsAdopted(versions.length);
  const name =
    only.document_kind === 'bylaws' ? documentKindLabel('bylaws') : (only.label ?? only.title);
  return w.versionAdopted(name, displayDate(only.adopted_on));
}

function committeesSummary(committees: readonly Form990Committee[]): string {
  if (committees.length === 0) return w.noCommittees;
  const yes = committees.filter((committee) => {
    const answer = newestAnswer(committee);
    return answer !== null && answer.choice_value !== 'none';
  });
  const [one] = yes;
  const answer = one === undefined ? null : newestAnswer(one);
  // The frame's form: the committee answered yes, in the organization's words; the opened line lists
  // every committee active in the year with its answer.
  if (yes.length === 1 && one !== undefined && answer !== null) {
    return w.committeeAnswered(one.name, w.answerInSummary(answer.choice_value));
  }
  if (yes.length > 1) return w.committeesAnsweredYes(yes.map((committee) => committee.name));
  return committees.some((committee) => newestAnswer(committee) !== null)
    ? w.noCommitteeAnsweredYes
    : w.committeesNotAnswered;
}

/** The committees as the opened line lists them: those standing, then those dissolved (frame p2). */
export function committeesInOrder(committees: readonly Form990Committee[]): Form990Committee[] {
  return [
    ...committees.filter((committee) => committee.status !== 'dissolved'),
    ...committees.filter((committee) => committee.status === 'dissolved'),
  ];
}

/**
 * Line 12c: the kinds the meetings the read serves hold, wherever they sit — a recusal and a
 * determination at one meeting read as both, as at two — or a dash (the page doesn't read the
 * register).
 */
function conflictsSummary(conflicts: readonly Form990ConflictMeeting[]): string | null {
  const recusals = conflicts.some((meeting) => meeting.recusal_recorded);
  const determinations = conflicts.some((meeting) => meeting.determination_recorded);
  if (recusals && determinations) return w.recusalsAndDeterminationsInMinutes;
  if (recusals) return w.recusalsInMinutes;
  if (determinations) return w.determinationsInMinutes;
  return null;
}

/** Line 1a: the seats listed on the served day, else the committees answered "broad" (§4.3.1). */
function directorsLineSummary(evidence: Form990Evidence, kinds: readonly string[]): string | null {
  const seats = evidence.directors.seats.length;
  if (seats > 0) return w.directorsSummary(displayDate(evidence.directors.as_of), seats);
  const broad = kinds.includes('committees') ? broadCommittees(evidence.committees) : [];
  const [only] = broad;
  if (only === undefined) return null;
  return broad.length === 1
    ? w.committeeAnswered(only.name, w.answerInSummary('broad'))
    : w.committeesAnsweredBroad(broad.map((committee) => committee.name));
}

/**
 * Line 11a: the year's current copy (the one provided last, first in the read), in the plan's safe
 * words when every director recorded at the act was provided it (P2: the read's own day), or a dash.
 */
function returnCopiesSummary(copies: readonly Form990ReturnCopy[] | null): string | null {
  const current = copies?.[0];
  if (current === undefined) return null;
  const day = displayDate(current.provided_on);
  if (current.withdrawn_at !== null) return rw.line11aWithdrawn(day);
  if (current.director_count === 0) return rw.line11aNoDirectors(day);
  const through = current.directors_provided_through;
  if (through === null) {
    return rw.line11aSome(day, current.directors_provided_count, current.director_count);
  }
  return through === current.provided_on
    ? rw.line11aEveryOn(day)
    : rw.line11aEveryBy(displayDate(through));
}

/** Line 11b: the board's own act linked as its review, named by the meeting's own title and day. */
function returnReviewSummary(reviews: readonly Form990ReturnReview[] | null): string | null {
  const review = reviews?.[0];
  if (review === undefined) return null;
  if (review.motion !== null) {
    const title = review.motion.meeting_title ?? '';
    return review.motion.held_on === null
      ? rw.line11bAct(title)
      : rw.line11bMeeting(title, displayDate(review.motion.held_on));
  }
  return rw.line11bAct(review.written_consent?.title ?? rw.untitledConsent);
}

/** One line's decisions (15a or 15b), newest decided first as the read serves them. */
export function compensationForLine(
  decisions: readonly Form990CompensationDecision[],
  line: string,
): Form990CompensationDecision[] {
  return decisions.filter((decision) => decision.form_990_line === line);
}

/** The line's people in the read's order (each by their newest decision), with their decisions. */
export function compensationPeople(
  decisions: readonly Form990CompensationDecision[],
): { key: string; decisions: Form990CompensationDecision[] }[] {
  const people = new Map<string, Form990CompensationDecision[]>();
  for (const decision of decisions) {
    const held = people.get(decision.person_key);
    if (held === undefined) people.set(decision.person_key, [decision]);
    else held.push(decision);
  }
  return [...people].map(([key, held]) => ({ key, decisions: held }));
}

/**
 * Lines 15a and 15b: whose pay the line's decisions set and the newest day decided in the year, or,
 * when the year holds none, the newest before it ("last decided"); a dash with none placed.
 */
function compensationSummary(
  decisions: readonly Form990CompensationDecision[],
  line: string,
): string | null {
  const people = compensationPeople(compensationForLine(decisions, line));
  const [first] = people;
  if (first === undefined) return null;
  const placed = people.flatMap((person) => person.decisions);
  const inYear = placed.filter((decision) => decision.placement === 'in_the_year');
  const newest = [...(inYear.length > 0 ? inYear : placed)].sort((a, b) =>
    b.decided_on.localeCompare(a.decided_on),
  )[0];
  if (newest === undefined) return null;
  const names =
    people.length === 1
      ? [
          w.personAndPosition(
            first.decisions[0]?.person_name ?? '',
            first.decisions[0]?.position ?? '',
          ),
        ]
      : people.map((person) => person.decisions[0]?.person_name ?? '');
  // The day kept on one line, so a narrow column wraps before it, never inside it.
  return w.line15Summary(names, oneLine(displayDate(newest.decided_on)), inYear.length === 0);
}

/**
 * One line's summary, or `null` for a dash. Read off the line's own `evidence_kinds` — never its
 * number — so a kind the backend adds to a line reaches the summary only once this page reads it.
 */
export function lineSummary(
  line: Form990Line,
  evidence: Form990Evidence,
  taxYear: Form990TaxYear,
): string | null {
  const kinds = line.evidence_kinds;
  if (kinds.includes('directors')) return directorsLineSummary(evidence, kinds);
  if (kinds.includes('disclosure_cycles')) {
    const labels = cyclesForLine(evidence.disclosure_cycles, line.line).map((cycle) => cycle.label);
    if (labels.length === 0) return null;
    return line.line === '12b' ? w.disclosures(labels) : w.answersIn(labels);
  }
  if (kinds.includes('governing_document_versions')) {
    return versionsSummary(evidence.governing_document_versions);
  }
  if (kinds.includes('board_meetings')) {
    const { meetings, written_consents: consents } = evidence.board_meetings;
    return meetings.length + consents.length === 0
      ? null
      : w.meetingsAndConsents(meetings.length, consents.length);
  }
  if (kinds.includes('committees')) return committeesSummary(evidence.committees);
  const policy = policyKindOf(kinds);
  if (policy !== null) return policySummary(evidence[policy], taxYear);
  if (kinds.includes('conflicts')) return conflictsSummary(evidence.conflicts);
  if (kinds.includes('return_copies')) return returnCopiesSummary(evidence.return_copies);
  if (kinds.includes('return_review')) return returnReviewSummary(evidence.return_review);
  if (kinds.includes('compensation_decisions')) {
    return compensationSummary(evidence.compensation_decisions, line.line);
  }
  return null;
}

/**
 * The minutes' cell under the column "Minutes": approved, the day and the way; otherwise the minute
 * book's own word for the minutes' state (D-CBG-9). A committee's (line 8b) would say its members
 * where the board's words say its directors: for the signatures' way, which the read never serves
 * for a meeting's own minutes today (line 8a's completeness case; the backend's
 * `board-form-990.md` §4.3.3).
 */
export function minutesCell(
  minutes: Form990Minutes | null,
  body: 'board' | 'committee' = 'board',
): string {
  if (minutes === null) return '—';
  if (minutes.state === 'approved') {
    if (minutes.approved_on === null) return w.minutesApproved;
    const way =
      (body === 'committee' ? w.committeeApprovedWay(minutes.approved_way) : null) ??
      w.approvedWay(minutes.approved_way);
    return w.approvedOnWay(displayDate(minutes.approved_on), way);
  }
  if (minutes.state === 'imported') return w.imported;
  if (minutes.state === 'submitted') return w.minutesAwaiting;
  return w.minutesDraft;
}

/** A line-8 date, marked when no later meeting is recorded: the date can still move (the clock's rule). */
export function line8Cell(line8On: string, nextMeetingOn: string | null): string {
  const day = displayDate(line8On);
  return nextMeetingOn === null ? w.line8Open(day) : day;
}

/** The disclaimer registry surfaces a read's class references stand for (§7); others are skipped. */
const CLASS_SURFACES = {
  'evidence-not-conclusion': 'BOARD_EVIDENCE_NOT_CONCLUSION',
  'not-your-lawyer': 'BOARD_NOT_YOUR_LAWYER',
} as const;

export type Form990ClassSurface = (typeof CLASS_SURFACES)[keyof typeof CLASS_SURFACES];

export function classSurfaces(ids: readonly string[]): Form990ClassSurface[] {
  return ids.flatMap((id) =>
    Object.hasOwn(CLASS_SURFACES, id) ? [CLASS_SURFACES[id as keyof typeof CLASS_SURFACES]] : [],
  );
}
